import { type ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { OrganizationApiKeyCompletionRequirement } from "graphql_globals";
import { Heading, Paragraph } from "common/core/typography";
import { DOCUMENT_SUPPORT_URL } from "constants/support";
import Link from "common/core/link";
import zillowLogo from "assets/images/logos/partners/zillow.png";
import usaaLogo from "assets/images/logos/partners/usaa.png";
import principalLogo from "assets/images/logos/partners/principal.png";
import atmbLogo from "assets/images/logos/partners/anytime-mailbox.png";
import CustomBlock from "common/signer/bit_landing/content/custom_block";
import { STATS, Stats } from "common/signer/bit_landing/v3/sections/stats";
import { ProofFooter } from "common/proof_footer";

import { getCompletionRequirementFromEasylinkRequirement } from "../shared/util";
import type { Branding_viewer_referralInfo_organizationBrand as OrganizationBrand } from "../branding_query.graphql";
import Styles from "./index.module.scss";
import { HowThisWorksSection } from "./how_this_works";

type Props = {
  organizationBrand: OrganizationBrand | null;
  easylinkRequirement?: OrganizationApiKeyCompletionRequirement;
  brandedFooter?: string | null;
  forReferral: boolean;
  forEasylink?: boolean;
  hideSocialProof?: boolean;
};

function HowThisWorks({
  easylinkRequirement,
  isRetail,
}: {
  easylinkRequirement: OrganizationApiKeyCompletionRequirement;
  isRetail: boolean;
}) {
  if (
    easylinkRequirement === OrganizationApiKeyCompletionRequirement.IDENTIFY ||
    easylinkRequirement === OrganizationApiKeyCompletionRequirement.VERIFY
  ) {
    return null;
  }

  const completionRequirement =
    getCompletionRequirementFromEasylinkRequirement(easylinkRequirement);

  if (isRetail) {
    return <HowThisWorksSection completionRequirement={completionRequirement} />;
  }

  return null;
}

function UploadLowerContent({
  easylinkRequirement = OrganizationApiKeyCompletionRequirement.NOTARIZATION,
  brandedFooter = null,
  hideSocialProof = false,
  isRetail = false,
}: Omit<Props, "organizationBrand" | "forReferral" | "forEasylink"> & {
  isRetail: boolean;
}) {
  return (
    <>
      {brandedFooter && (
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: brandedFooter }}
        />
      )}
      {easylinkRequirement === OrganizationApiKeyCompletionRequirement.NOTARIZATION && (
        <NotarizationFaq />
      )}
      {!brandedFooter &&
        !hideSocialProof &&
        easylinkRequirement !== OrganizationApiKeyCompletionRequirement.IDENTIFY &&
        easylinkRequirement !== OrganizationApiKeyCompletionRequirement.VERIFY && (
          <div className={Styles.socialProof} data-growth-id="partner-logos-container">
            <Heading level="h3" textStyle="headingSix">
              <FormattedMessage
                id="ae9f060f-887a-43a4-8679-e4ff5432d5d8"
                defaultMessage="Trusted by 7000+ businesses, including..."
              />
            </Heading>
            <div className={Styles.logosContainer}>
              <div className={Styles.logo} data-growth-id="partner-logo">
                <img src={usaaLogo} alt="USAA" />
              </div>
              <div className={Styles.logo} data-growth-id="partner-logo">
                <img src={zillowLogo} alt="Zillow" />
              </div>
              <div className={Styles.logo} data-growth-id="partner-logo">
                <img src={atmbLogo} alt="Anytime Mailbox" />
              </div>
              <div className={Styles.logo} data-growth-id="partner-logo">
                <img src={principalLogo} alt="Principal" />
              </div>
            </div>
          </div>
        )}
      {hideSocialProof &&
        easylinkRequirement === OrganizationApiKeyCompletionRequirement.NOTARIZATION && (
          <Stats
            stats={[
              STATS.states,
              STATS.onlineNotarizations,
              STATS.trustPilot,
              STATS.meetingCompletionBusinessRon,
            ]}
          />
        )}
      {easylinkRequirement === OrganizationApiKeyCompletionRequirement.IDENTIFY && <IdentifyFaq />}
      {easylinkRequirement === OrganizationApiKeyCompletionRequirement.VERIFY && <VerifyFaq />}
      <HowThisWorks easylinkRequirement={easylinkRequirement} isRetail={isRetail} />
    </>
  );
}

function NotarizationFaq() {
  const sections = [
    {
      heading: (
        <FormattedMessage
          id="ae9f060f-887a-43a4-8679-e4ff5432d5d8"
          defaultMessage="What is online notarization and is it legal?"
        />
      ),
      content: (
        <Paragraph>
          <FormattedMessage
            id="f9225b76-5f32-4b86-80ca-914df2ce6879"
            defaultMessage="Online notarization allows a person to appear before a commissioned notary public in real-time using audio/video technology. A document notarized online is as legally valid as one notarized via traditional paper-based notarization."
          />
        </Paragraph>
      ),
    },
    {
      heading: (
        <FormattedMessage
          id="5affc22c-0532-420a-a5a9-847c5f18b776"
          defaultMessage="What documents can I get notarized on the platform?"
        />
      ),
      content: (
        <Paragraph>
          <FormattedMessage
            id="c58e634b-2e3a-466f-ac6c-35c9842341b0"
            defaultMessage="Nearly every document can be notarized on the platform. However, certain documents are restricted, including marriage certificates and documents intended for use outside of the United States. <Link>View the full list</Link>"
            values={{
              Link: (text) => (
                <Link white href={DOCUMENT_SUPPORT_URL}>
                  {text}
                </Link>
              ),
            }}
          />
        </Paragraph>
      ),
    },
    {
      heading: (
        <FormattedMessage
          id="c3a4ca2a-0fc5-42ae-a1cf-9818cfe20ad3"
          defaultMessage="Can two or more signers get their signatures notarized?"
        />
      ),
      content: (
        <>
          <Paragraph>
            <FormattedMessage
              id="10a97f55-cc2c-49a4-80ab-c65d16a6e95a"
              defaultMessage="Yes! Up to 9 additional signers can be added to a notarization. Signers have the flexibility to:"
            />
          </Paragraph>
          <ul className={Styles.faqList}>
            <li>
              <FormattedMessage
                id="f82020c6-315b-4ae7-aeb9-936ee5d0d1fc"
                defaultMessage="Join the same notary meeting using the same device."
              />
            </li>
            <li>
              <FormattedMessage
                id="314d63f7-0d13-49eb-afbe-844a3d421dc5"
                defaultMessage="Join the same notary meeting using different devices."
              />
            </li>
            <li>
              <FormattedMessage
                id="181e0ac6-9852-40dd-b1ab-964603c17477"
                defaultMessage="Participate in separate notary meetings from different devices."
              />
            </li>
          </ul>
          <Paragraph>
            <FormattedMessage
              id="b99f863c-0969-4099-a0cb-032977ff4647"
              defaultMessage="Please note that additional fees apply: $5 for each additional signer and $25 for each additional notary meeting."
            />
          </Paragraph>
        </>
      ),
    },
  ];

  return <Faq sections={sections} />;
}

function IdentifyFaq() {
  return (
    <Faq
      sections={[
        {
          heading: (
            <FormattedMessage
              id="7c7626c7-3152-4107-8180-904abbb30df3"
              defaultMessage="What is Proof?"
            />
          ),
          content: (
            <Paragraph>
              <FormattedMessage
                id="cb5b5a76-a47c-425e-bc30-7c31b97ea2fb"
                defaultMessage="For the important things you do online, you may be asked to verify your identity. This is often the first step to opening a new account, submitting an application or authorizing account changes. You’re here because a business needs to verify your ID in order to proceed."
              />
            </Paragraph>
          ),
        },
        {
          heading: (
            <FormattedMessage
              id="e45cc895-776d-4003-8ff1-30cf25bc06bf"
              defaultMessage="How does this work?"
            />
          ),
          content: (
            <Paragraph>
              <FormattedMessage
                id="7497c779-a2f4-43c9-972f-de7e3d7b3c49"
                defaultMessage="This only takes 5 minutes and can be completed from any type of device. You’ll be asked a series of questions and you may need to have your passport, driver’s license or ID documents handy. Should you run into any issues, you may be provided with an option to connect with a live agent over video to assist you."
              />
            </Paragraph>
          ),
        },
        {
          heading: (
            <FormattedMessage
              id="3a656266-7a5e-4c15-8ddc-05f69912b69f"
              defaultMessage="What happens afterwards?"
            />
          ),
          content: (
            <Paragraph>
              <FormattedMessage
                id="2413008d-8cc9-4d30-b554-7224d4da1f12"
                defaultMessage="Once you have completed all verification steps, the business who requested your identity verification will get notified and get access to an identity report. This report provides details on how your identity was verified and serves as a record that you’ve successfully completed the process. It’s like a receipt, it’s proof that you’ve been verified."
              />
            </Paragraph>
          ),
        },
      ]}
    />
  );
}

export function VerifyFaq() {
  return (
    <Faq
      sections={[
        {
          heading: (
            <FormattedMessage
              id="0260960f-13e5-4604-b551-479ee140e419"
              defaultMessage="What is Proof?"
            />
          ),
          content: (
            <Paragraph>
              <FormattedMessage
                id="4b5b8eca-e1fa-483b-88a6-b96a96032264"
                defaultMessage="For the important things you do online, you may be asked to verify your identity. This is often the first step to opening a new account, submitting an application or authorizing account changes. You’re here because a business needs to verify your ID in order to proceed."
              />
            </Paragraph>
          ),
        },
        {
          heading: (
            <FormattedMessage
              id="bfb0a35b-be9e-436d-8f4d-2bf6b343a648"
              defaultMessage="How does this work?"
            />
          ),
          content: (
            <Paragraph>
              <FormattedMessage
                id="d08f7f72-b2f7-49a8-87cf-824fbc93e812"
                defaultMessage="This only takes 10 minutes and can be completed from any type of device. You’ll be asked some personal information and you may need to have your passport, driver’s license or ID documents handy."
              />
            </Paragraph>
          ),
        },
        {
          heading: (
            <FormattedMessage
              id="3a656266-7a5e-4c15-8ddc-05f69912b69f"
              defaultMessage="What happens afterwards?"
            />
          ),
          content: (
            <Paragraph>
              <FormattedMessage
                id="e8936b12-1ed6-43bb-ae8d-775c7357107f"
                defaultMessage="Once you have completed all verification steps, the business who requested your identity verification will get notified and get access to an identity report. This report provides details on how your identity was verified and serves as a record that you’ve successfully completed the process. It’s like a receipt, it’s proof that you’ve been verified."
              />
            </Paragraph>
          ),
        },
      ]}
    />
  );
}

function Faq(props: { sections: { heading: ReactNode; content: ReactNode }[] }) {
  return (
    <div className={Styles.faqWrapper}>
      <div className={Styles.content} data-growth-id="faq-container">
        {props.sections.map((section, index) => (
          <div key={index} className={Styles.section} data-growth-id="faq-section">
            <Heading level="h3" textStyle="headingSix">
              {section.heading}
            </Heading>
            {section.content}
          </div>
        ))}
      </div>
    </div>
  );
}

function BrandAndLowerContent({
  organizationBrand,
  easylinkRequirement,
  brandedFooter,
  forReferral,
  forEasylink,
}: Props) {
  const isRetail = !forReferral && !forEasylink;

  return (
    <>
      {Boolean(organizationBrand?.signerLandingPageBodyContent.length) && (
        <CustomBlock organizationBrand={organizationBrand!} />
      )}

      <UploadLowerContent
        isRetail={isRetail}
        easylinkRequirement={easylinkRequirement}
        brandedFooter={brandedFooter}
        hideSocialProof={Boolean(organizationBrand)}
      />

      <div className={Styles.footer}>
        <ProofFooter />
      </div>
    </>
  );
}

export default BrandAndLowerContent;
